/**
 * Status bar (reference 02): open level path, entity and selection counts, the
 * render API that was actually acquired, and the console hint. While playing,
 * it says so instead (reference 05).
 */
import { useEditor } from "../EditorContext";
import { useStore } from "../hooks/useStore";
import type { PlayState } from "../play/PlaySession";
import { useLoadProgress, useSelection, useWorldSnapshot } from "../hooks/editorHooks";

export function StatusBar() {
  const { play } = useEditor();
  const playing = useStore(play);
  return playing ? <PlayingStatus state={playing} /> : <EditingStatus />;
}

/** While playing, the bar says so, in the play colors (reference 05). */
function PlayingStatus({ state }: { state: PlayState }) {
  return (
    <footer className="kn-statusbar" data-playing="true">
      <span className="kn-statusbar__playing">Playing in Editor</span>
      <span>
        {state.level} · {state.mode}
        {state.paused ? " · Paused" : ""}
      </span>
      <span className="kn-statusbar__spacer" />
      <span>Esc to stop</span>
    </footer>
  );
}

function EditingStatus() {
  const { content, render, dispatch } = useEditor();
  const world = useWorldSnapshot();
  const selection = useSelection();
  const progress = useLoadProgress();

  const level = content.index.levels.find((entry) => entry.id === world.levelId);
  const loading = progress.phase === "loading";
  const percent = progress.bytesTotal
    ? Math.round((progress.bytesLoaded / progress.bytesTotal) * 100)
    : 0;

  return (
    <footer className="kn-statusbar" data-loading={loading}>
      {loading ? (
        <span className="kn-statusbar__loading">
          Loading {progress.assetPath} <span>{percent}%</span>
        </span>
      ) : (
        <span>{level?.assetPath ?? "No level"}</span>
      )}
      <span className="kn-statusbar__secondary">
        {world.entities.size} entities · {selection.ids.length} selected
      </span>
      <span className="kn-statusbar__spacer" />
      <span>{render.backend.info.api}</span>
      <button
        type="button"
        className="kn-statusbar__console"
        onClick={() =>
          void dispatch({ type: "panel.focus", payload: { panel: "console" }, origin: "user" })
        }
      >
        ~ console
      </button>
    </footer>
  );
}
